/* Árvore do fluxo e alcance das etapas.
 *
 * Lógica pura sobre a lista de `actions` — sem import da árvore de componentes,
 * como em `stepReferences.js`. O canvas desenha a partir daqui, e o editor usa a
 * mesma leitura para dizer quais etapas nunca executam.
 */

/** Passos que abrem faixas no canvas: o desvio, uma por caminho, e a junção,
 * a faixa única em que os caminhos unidos passam a seguir. */
const opensLanes = (action) => action.type === "switch" || action.type === "merge";

/** Caminho declarado por uma junção, no escopo em que ela executa. */
const routeOfMerge = (action) => {
    const config = action.config || {};
    const prefix = action.branch ? `${action.branch}.` : "";
    return {
        path: config.name ? prefix + config.name : null,
        label: config.label || config.name || "(nova junção — defina o nome)",
    };
};

/** Rotas declaradas por um desvio ou por uma junção, como caminhos completos.
 * Rotas ainda sem nome (recém-adicionadas no editor) entram com `path: null`
 * para aparecerem no canvas como pendentes, em vez de sumirem. */
const routesOfNode = (action) => {
    if (action.type === "merge") return [routeOfMerge(action)];

    const prefix = action.branch ? `${action.branch}.` : "";
    const config = action.config || {};
    const routes = Array.isArray(config.routes) ? config.routes : [];
    return routes
        .filter((route) => !!route)
        .map((route) => ({
            path: route.name ? prefix + route.name : null,
            label: route.label || route.name || "(novo caminho — defina o nome)",
        }));
};

/**
 * O que o desvio faz quando nenhuma rota casa (`config.fallback`), na mesma
 * leitura do executor (`action-switch.use-case.ts`): `abort` é o padrão de
 * todos os desvios já salvos, e `route` sem caminho escolhido cai nele.
 */
const switchFallback = (action) => {
    const fallback = action.config?.fallback;
    if (fallback?.action === "continue") return "continue";
    if (fallback?.action === "route" && fallback.name) return "route";
    return "abort";
};

/**
 * Se o desvio devolve o fluxo ao tronco quando nenhuma rota casa. É a saída
 * que o canvas precisa desenhar quando nenhum caminho converge: sem ela, a
 * continuação fica pendurada num conector sem origem.
 */
export const switchContinuesOnNoMatch = (action) =>
    action.type === "switch" && switchFallback(action) === "continue";

/** Caminhos de origem de uma junção, ou `null` se o passo não é uma junção
 * configurada — só com 2 ou mais origens ela une alguma coisa. */
const mergeSourcesOf = (action) => {
    if (action.type !== "merge") return null;
    const sources = Array.isArray(action.config?.sources) ? action.config.sources : [];
    return sources.length >= 2 ? sources : null;
};

const underPath = (branch, path) =>
    branch === path || (!!branch && branch.startsWith(`${path}.`));

/**
 * Converte a lista linear de actions na árvore do canvas. Um `switch` consome
 * os passos seguintes que pertencem às suas rotas (incluindo aninhados) e os
 * distribui em faixas; o primeiro passo fora delas reconverge para o tronco.
 *
 * Uma junção que une caminhos do desvio anterior é absorvida pelo bloco dele:
 * as faixas unidas viram um grupo que termina no nó da junção, e a continuação
 * comum sai desse nó — as faixas que ela não une seguem inteiras até a saída do
 * bloco. É o que separa, no desenho, a convergência parcial da total. Fora
 * desse caso (junção de caminhos de outro escopo, ou ainda sem origens), ela
 * abre uma faixa própria, como um desvio de um caminho só.
 */
const buildLevel = (items) => {
    const nodes = [];
    let i = 0;
    while (i < items.length) {
        const current = items[i];
        const node = { item: current };
        nodes.push(node);

        if (opensLanes(current.action)) {
            const routes = routesOfNode(current.action);
            const span = [];
            let j = i + 1;
            while (j < items.length) {
                const branch = items[j].action.branch;
                if (branch && routes.some((route) => route.path && underPath(branch, route.path))) {
                    span.push(items[j]);
                    j++;
                } else {
                    break;
                }
            }
            const spanLastIndex = span.length
                ? span[span.length - 1].index
                : current.index;
            let lanes = routes.map((route) => {
                const childItems = route.path
                    ? span.filter((item) =>
                        underPath(item.action.branch, route.path)
                    )
                    : [];
                return {
                    ...route,
                    children: buildLevel(childItems),
                    lastIndex: childItems.length
                        ? childItems[childItems.length - 1].index
                        : null,
                };
            });

            // Junções seguidas são absorvidas em sequência: a rota que uma
            // declara pode ser origem da próxima (junção de junção).
            let lastIndex = spanLastIndex;
            while (j < items.length) {
                // Fotografia das faixas desta volta: `lanes` é reatribuído no
                // fim do laço, e os fechamentos abaixo falam das faixas atuais.
                const openLanes = lanes;
                const sources = mergeSourcesOf(items[j].action);
                if (!sources || !sources.every((source) =>
                    openLanes.some((lane) => lane.path === source)
                )) {
                    break;
                }

                const mergeItem = items[j];
                const route = routeOfMerge(mergeItem.action);
                j++;
                const groupSpan = [];
                // `route.path` nulo (junção ainda sem nome) não casa com passo
                // nenhum: sem essa guarda, `branch: null` do tronco entraria na
                // continuação da junção.
                while (j < items.length && route.path &&
                    underPath(items[j].action.branch, route.path)) {
                    groupSpan.push(items[j]);
                    j++;
                }

                const merged = new Set(sources);
                const group = {
                    kind: "group",
                    item: mergeItem,
                    ...route,
                    lanes: openLanes.filter((lane) => merged.has(lane.path)),
                    children: buildLevel(groupSpan),
                    lastIndex: groupSpan.length
                        ? groupSpan[groupSpan.length - 1].index
                        : mergeItem.index,
                };
                // O grupo ocupa a posição da primeira faixa que ele une, para
                // as faixas de fora manterem a ordem em que foram declaradas.
                const remaining = [];
                let placed = false;
                openLanes.forEach((lane) => {
                    if (!merged.has(lane.path)) {
                        remaining.push(lane);
                        return;
                    }
                    if (!placed) {
                        remaining.push(group);
                        placed = true;
                    }
                });
                lanes = remaining;
                lastIndex = group.lastIndex;
            }

            node.branches = lanes;
            // Onde entra um passo novo de uma faixa vazia: antes das junções
            // absorvidas, senão ele cairia depois da convergência.
            node.laneLastIndex = spanLastIndex;
            // Onde continua o tronco depois do bloco inteiro.
            node.blockLastIndex = lastIndex;
            i = j;
        } else {
            i++;
        }
    }
    return nodes;
};

export const buildFlowTree = (actions) =>
    buildLevel(actions.map((action, index) => ({ action, index })));

/** Faixa encerrada no desenho: o último passo dela é um "Encerrar fluxo".
 *
 * É deliberadamente literal — decide se a faixa oferece o "+" e se ela se liga
 * ao merge. Uma faixa que termina num desvio já fechado continua aceitando
 * passos novos (o usuário pode estar reorganizando o fluxo); que esses passos
 * não executam é o que `findUnreachableSteps` diz, sem tirar a ação de ninguém. */
export const laneIsEnded = (branch) => {
    const lastChild = branch.children[branch.children.length - 1];
    return !!lastChild && lastChild.item.action.type === "end_flow";
};

/* --- Alcance das etapas --------------------------------------------------
 *
 * O executor percorre os passos em ordem e para no primeiro "Encerrar fluxo"
 * que alcança (`execute-automations.use-case.ts`). Um passo pulado — rota
 * inativa — não interrompe nada: entra no log como `skipped` e o laço segue.
 *
 * Daí as duas formas de um passo ficar inalcançável:
 *
 *  - um "Encerrar fluxo" antes dele, no mesmo escopo;
 *  - um desvio antes dele cujos caminhos declarados se fecham todos E que
 *    encerra a automação quando nenhuma rota casa. As duas condições contam:
 *    é o desfecho "nenhum caminho casou" que decide se ainda existe execução
 *    chegando ao tronco depois do bloco.
 *
 * Daí o peso do `fallback` do desvio (ver `switchFallback`):
 *
 *  - `abort` (padrão) e `route` → toda execução termina dentro dos caminhos,
 *    então caminhos todos fechados fecham também o tronco;
 *  - `continue` → nenhuma rota fica ativa e o fluxo segue no primeiro passo
 *    fora do desvio. Os caminhos podem terminar todos em "Encerrar fluxo" e a
 *    continuação ainda executa: ela é o desfecho do "nenhum caminho casou",
 *    não um trecho morto.
 *
 * A junção entra pela mesma porta: ela só executa se alguma origem chegar.
 */

/** Se o fluxo deste nível não passa de algum passo dele. */
const levelCloses = (nodes) => nodes.some(nodeCloses);

const nodeCloses = (node) => {
    if (node.item.action.type === "end_flow") return true;
    // Só o desvio: a junção que não recebe origem nenhuma é pulada, e o tronco
    // depois dela continua executando normalmente.
    if (node.item.action.type !== "switch") return false;
    if (!node.branches || !node.branches.length) return false;
    // "Continuar no fluxo principal": o desvio entrega o fluxo ao primeiro
    // passo fora dele quando nenhuma rota casa. Esse desfecho existe sempre —
    // o que os caminhos fazem não o cancela —, então o bloco nunca fecha o
    // tronco.
    if (switchFallback(node.item.action) === "continue") return false;
    return node.branches.every(branchCloses);
};

/**
 * Se o caminho se fecha sem entregar nada adiante — o que faz a saída dele
 * (a curva até o merge, a linha elástica) ser fluxo morto no desenho.
 * Mais amplo que `laneIsEnded`: cobre também o caminho que termina num desvio
 * aninhado com todas as rotas encerradas.
 */
export const laneClosesFlow = (branch) => branchCloses(branch);

const branchCloses = (branch) => {
    if (branch.kind === "group") {
        // Nenhuma origem chega: a junção e a continuação dela não executam.
        if (branch.lanes.every(branchCloses)) return true;
        return levelCloses(branch.children);
    }
    // Caminho ainda sem nome não recebe passo nenhum — não fecha nada.
    if (!branch.path) return false;
    return levelCloses(branch.children);
};

const collectBranch = (branch, out, cause) => {
    if (branch.kind === "group") {
        branch.lanes.forEach((lane) => collectBranch(lane, out, cause));
        out.set(branch.item.index, cause);
    }
    collectLevel(branch.children, out, cause);
};

const collectNode = (node, out, cause) => {
    out.set(node.item.index, cause);
    if (node.branches) {
        node.branches.forEach((branch) => collectBranch(branch, out, cause));
    }
};

const collectLevel = (nodes, out, cause) =>
    nodes.forEach((node) => collectNode(node, out, cause));

const markBranch = (branch, out) => {
    if (branch.kind !== "group") {
        markLevel(branch.children, out);
        return;
    }
    branch.lanes.forEach((lane) => markBranch(lane, out));
    if (branch.lanes.every(branchCloses)) {
        // `null` como causa: não é um passo que encerra, é a ausência de
        // origem — a junção nunca é alcançada.
        out.set(branch.item.index, null);
        collectLevel(branch.children, out, null);
        return;
    }
    markLevel(branch.children, out);
};

const markLevel = (nodes, out) => {
    let cause = null;
    let open = true;
    nodes.forEach((node) => {
        if (!open) {
            collectNode(node, out, cause);
            return;
        }
        if (node.branches) {
            node.branches.forEach((branch) => markBranch(branch, out));
        }
        if (nodeCloses(node)) {
            open = false;
            cause = node.item.index;
        }
    });
};

/**
 * Etapas que nunca executam, como `Map<posição, posição que fecha o fluxo>`.
 * A causa é `null` quando o passo é uma junção (ou a continuação dela) que não
 * recebe origem nenhuma.
 */
export const findUnreachableStepsFromTree = (tree) => {
    const out = new Map();
    markLevel(tree, out);
    return out;
};

export const findUnreachableSteps = (actions) =>
    findUnreachableStepsFromTree(buildFlowTree(actions ?? []));
